import { useEffect, useState } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';

import useBackInStockWaitlistItemsQuery from 'api/queries/useBackInStockWaitlistItemsQuery';
import usePreferencesQuery from 'api/queries/usePreferencesQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import { incrementRecurringSessionCount, shouldShowRecurringLimitWarning } from 'features/shop/recurringModals';
import { Alert } from 'honeydew/Alert';
import RecurringIcon from 'honeydew/icons/FoodPreferences/RecurringIcon';
import useGlobalStore from 'hooks/useGlobalStore';
import useUpdatePreference from 'hooks/useUpdatePreference';
import DrawerModal from 'shared/DrawerModal';
import { trackEvent } from 'utils/analyticsUtils';

import { BackInStockWaitlistOptionButton } from './BackInStockWaitlistOptionButton';
import useAddBackInStockWaitlistItem from './useAddBackInStockWaitlistItem';
import useRemoveBackInStockWaitlistItem from './useRemoveBackInStockWaitlistItem';
import useWaitlistedItemIdsQuery, { getWaitlistRequestId } from './useWaitlistedItemIdsQuery';

const WAITLIST_ERROR_MESSAGE = 'Something went wrong updating the waitlist. Please try again.';

/**
 * Modal for joining the back-in-stock waitlist and optionally adding to recurring.
 *
 * @param {Object} props
 * @param {boolean} [props.isOpen=false] - Whether the modal is open
 * @param {import('features/shop/ProductCardV2/types').ProductData|null} [props.productData] - Product to display
 * @param {Function} props.onClose - Callback when modal is closed
 */
export function BackInStockWaitlistModal({ isOpen = false, onClose, productData }) {
    const [waitlistError, setWaitlistError] = useState('');
    const { data: preferences = {} } = usePreferencesQuery();
    const { data: subscription } = useSubscriptionQuery();
    const { data: waitlistedItemIds = [] } = useWaitlistedItemIdsQuery({
        queryProps: { enabled: isOpen },
    });
    const { data: waitlistItems = [] } = useBackInStockWaitlistItemsQuery({
        queryProps: { enabled: isOpen },
    });

    const { isPending: isAddingToWaitlist, mutate: addToWaitlist } = useAddBackInStockWaitlistItem();
    const { isPending: isRemovingFromWaitlist, mutate: removeFromWaitlist } = useRemoveBackInStockWaitlistItem();
    const { backInStockWaitlistModal, setRecurringLimitWarningModal } = useGlobalStore();
    const { addRecurringUserPreference, removeUserPreference } = useUpdatePreference({
        preferenceID: productData?.externalSku,
    });

    useEffect(() => {
        if (!isOpen) {
            setWaitlistError('');
            return;
        }

        if (!productData) {
            return;
        }

        trackEvent('back in stock waitlist modal opened', {
            product_id: productData.id,
        });
    }, [isOpen, productData?.id]);

    if (!isOpen || !productData) {
        return null;
    }

    const { externalSku, id, name } = productData;
    const isAutoshipCustomer = subscription?.variant === 'autoship';

    const recurringItemExternalSkus = new Set(
        preferences.recurring?.[0]?.recurring?.map((item) => item?.id).filter(Boolean) ?? []
    );
    const isInRecurringList = externalSku ? recurringItemExternalSkus.has(externalSku) : false;
    const isOnWaitlist = waitlistedItemIds.includes(id);
    const waitlistRequestID = getWaitlistRequestId(waitlistItems, id);

    const isWaitlistPending = isAddingToWaitlist || isRemovingFromWaitlist;

    const handleWaitlistError = () => {
        setWaitlistError(WAITLIST_ERROR_MESSAGE);
    };

    const handleWaitlistClick = () => {
        if (isWaitlistPending) {
            return;
        }

        setWaitlistError('');

        if (isOnWaitlist) {
            if (waitlistRequestID == null) {
                handleWaitlistError();
                return;
            }

            removeFromWaitlist({ requestID: waitlistRequestID }, { onError: handleWaitlistError });
            trackEvent('back in stock waitlist item removed', {
                product_id: id,
                source: backInStockWaitlistModal.source ?? 'back-in-stock-waitlist modal',
            });
            return;
        }

        addToWaitlist({ sellableItemID: id }, { onError: handleWaitlistError });
        trackEvent('back in stock waitlist item joined', {
            product_id: id,
            source: backInStockWaitlistModal.source ?? 'back-in-stock-waitlist modal',
        });
    };

    const trackPreferenceChangeEvent = (changeType) => {
        trackEvent('item preference update', {
            new_preference: changeType,
            source: backInStockWaitlistModal.source ?? 'back-in-stock-waitlist modal',
            path: window.location.pathname,
            isNative: !!window.isNativeApp,
            product_name: name,
            product_id: externalSku,
            out_of_stock: true,
        });
    };

    const checkRecurringLimitWarning = () => {
        if (shouldShowRecurringLimitWarning()) {
            setRecurringLimitWarningModal(true);
            trackEvent('recurring warning modal opened', {
                path: window.location.pathname,
            });
        }
    };

    const handleAddToRecurring = () => {
        addRecurringUserPreference();
        trackPreferenceChangeEvent('recurring');
        incrementRecurringSessionCount();
        checkRecurringLimitWarning();
    };

    const handleRemoveFromRecurring = () => {
        removeUserPreference();
        trackPreferenceChangeEvent('none');
    };

    const handleRecurringClick = () => {
        if (!externalSku) {
            return;
        }

        if (isInRecurringList) {
            handleRemoveFromRecurring();
            return;
        }

        handleAddToRecurring();
    };

    return (
        <DrawerModal
            onClose={onClose}
            drawerProps={{
                sx: { zIndex: 6000 },
            }}
            modalProps={{
                centered: true,
                styles: { contentRoot: 'py-40 px-60' },
                includeHorizontalPadding: false,
            }}
        >
            <div className="flex flex-col">
                <h2 className="text-heading-xs mb-15 md:mb-10">
                    Get first dibs on {name} when it&apos;s back in stock:
                </h2>
                {waitlistError && (
                    <div className="mb-15 md:mb-10">
                        <Alert variant="error">{waitlistError}</Alert>
                    </div>
                )}
                <div className="flex flex-col items-start gap-10 md:flex-row">
                    <div className="flex w-full flex-col rounded-lg border border-grey-light bg-malt p-15 md:w-1/2 md:p-20">
                        <h3 className="font-grotesk-bold text-body-mobile-md md:text-body-desktop-md">
                            Join This Week&apos;s Waitlist
                        </h3>
                        <p className="mt-10 text-body-mobile-xs md:text-body-mobile-sm">
                            We&apos;ll automatically add it to your cart if it comes back in stock before your shopping
                            window closes.
                        </p>
                        <p className="mt-10 text-body-mobile-xs text-grey">
                            It will be charged in addition to your cart in the event it comes back in stock. Packaging
                            fees may apply for frozen or refrigerated products.
                        </p>
                        <BackInStockWaitlistOptionButton
                            active={isOnWaitlist}
                            activeLabel="On the Waitlist"
                            className="mt-15 w-full grow-0"
                            disabled={isWaitlistPending}
                            label="Join the Waitlist"
                            onClick={handleWaitlistClick}
                        >
                            <ClockIcon className="size-[24px] shrink-0 stroke-[2px]" />
                        </BackInStockWaitlistOptionButton>
                    </div>
                    {isAutoshipCustomer && !productData.isBundle && (
                        <div className="flex w-full flex-col rounded-lg border border-grey-light bg-malt p-15 md:w-1/2 md:p-20">
                            <h3 className="font-grotesk-bold text-body-mobile-md md:text-body-desktop-md">
                                Add to Weekly Recurring
                            </h3>
                            <p className="mt-10 text-body-mobile-xs md:text-body-mobile-sm">
                                We&apos;ll automatically add it to your cart each week it&apos;s available.
                            </p>
                            <p className="mt-10 text-body-mobile-xs text-grey">
                                Recurring items are charged in addition to your pre-filled cart (and are subject to
                                availability!).
                            </p>
                            <BackInStockWaitlistOptionButton
                                active={isInRecurringList}
                                activeLabel="Added to Recurring"
                                className="mt-15 w-full grow-0"
                                label="Add to Recurring"
                                onClick={handleRecurringClick}
                            >
                                <RecurringIcon width={24} />
                            </BackInStockWaitlistOptionButton>
                        </div>
                    )}
                </div>
            </div>
        </DrawerModal>
    );
}
